import type { PortfolioRow } from "@/portfolio/types";
import { t } from "@lingui/core/macro";
import { compile } from "ejs";
import type { PortfolioEnergyFeatureProperties } from "../portfolio-energy-poi/projection";
import template from "./templates/portfolioEnergy.ejs?raw";

const compiledTemplate = compile(template);

export type PortfolioEnergyPopoverEntry = Readonly<{
  properties: PortfolioEnergyFeatureProperties;
  row: PortfolioRow;
}>;

type ColumnKey =
  | "coordinates"
  | "asset"
  | "installedCapacity"
  | "turbineId"
  | "hubHeight"
  | "trackingType"
  | "tilt"
  | "orientation"
  | "criticalSnowdepth"
  | "currentCapacity";

type Column = Readonly<{
  key: ColumnKey;
  label: string;
}>;

function valueText(properties: PortfolioEnergyFeatureProperties) {
  switch (properties.valueState) {
    case "loading":
      return t`Loading`;
    case "missing":
      return t`No value`;
    case "failed":
      return t`Request failed`;
    case "stale":
      return t`No value for the selected time`;
    case "invalid-configuration":
      return t`Invalid power configuration`;
    case "loaded":
      return t`No value`;
  }
}

function installedCapacityKw(row: PortfolioRow) {
  const specification = row.specifications?.installedCapacityKw;
  if (typeof specification === "number" && Number.isFinite(specification)) {
    return specification;
  }
  const imported = row.properties?.["installed capacity [kW]"];
  return typeof imported === "number" && Number.isFinite(imported) ? imported : null;
}

function tableValue(value: unknown, unit = "") {
  return value == null || (typeof value === "string" && value.trim() === "") ? "—" : `${value}${unit}`;
}

function hasUsableValue(value: unknown) {
  return value != null && (typeof value !== "string" || value.trim() !== "");
}

function currentCapacity(properties: PortfolioEnergyFeatureProperties) {
  return properties.valueState === "loaded" && Number.isFinite(properties.valueMw)
    ? `${Math.round((properties.valueMw ?? 0) * 1000)} kW`
    : valueText(properties);
}

export function createPortfolioEnergyPopover(entries: readonly PortfolioEnergyPopoverEntry[]) {
  const data: Record<ColumnKey, string>[] = entries.map(({ properties, row }) => ({
    coordinates: `${Number(row.coordinates.lat).toFixed(4)}, ${Number(row.coordinates.lon).toFixed(4)}`,
    asset: properties.name || properties.assetId || t`Asset`,
    installedCapacity: tableValue(installedCapacityKw(row), " kW"),
    turbineId: tableValue(row.turbineId),
    hubHeight: tableValue(row.hubHeight, " m"),
    trackingType: tableValue(row.specifications?.trackingType),
    tilt: tableValue(row.specifications?.tiltDeg, "°"),
    orientation: tableValue(row.specifications?.orientationDeg, "°"),
    criticalSnowdepth: tableValue(row.specifications?.criticalSnowdepthCm, " cm"),
    currentCapacity: currentCapacity(properties),
  }));

  const columns: Column[] = [
    { key: "coordinates", label: t`Coordinates` },
    { key: "asset", label: t`Asset` },
    { key: "installedCapacity", label: t`Installed capacity` },
  ];
  if (entries[0]?.properties.technology === "wind") {
    columns.push({ key: "turbineId", label: t`Turbine ID` }, { key: "hubHeight", label: t`Hub height` });
  } else {
    const solarColumns: ReadonlyArray<Column & { value: (entry: PortfolioEnergyPopoverEntry) => unknown }> = [
      {
        key: "trackingType",
        label: t`Tracking type`,
        value: ({ row }) => row.specifications?.trackingType,
      },
      { key: "tilt", label: t`Tilt`, value: ({ row }) => row.specifications?.tiltDeg },
      { key: "orientation", label: t`Orientation`, value: ({ row }) => row.specifications?.orientationDeg },
      {
        key: "criticalSnowdepth",
        label: t`Critical snow depth`,
        value: ({ row }) => row.specifications?.criticalSnowdepthCm,
      },
    ];
    columns.push(...solarColumns.filter((column) => entries.some((entry) => hasUsableValue(column.value(entry)))));
  }
  columns.push({ key: "currentCapacity", label: t`Current capacity` });

  return compiledTemplate({
    columns,
    data,
  });
}
